import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { BreadcrumbListSchema } from '@/components/base/Breadcrumb';
import { WebPageSchema, ArticleSchema } from '@/components/base/SEOSchemas';
import { comics, comicCategories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const comic = useMemo(() => {
    return comics.find((c) => c.id === Number(id));
  }, [id]);

  const category = comic ? comicCategories.find((c) => c.slug === comic.category) : undefined;

  const breadcrumbItems = comic
    ? [
        { name: '首页', href: '/' },
        { name: `${category?.name || '漫画'}`, href: `/category/${comic.category}` },
        { name: comic.title, href: `/comic/${comic.id}` },
      ]
    : [{ name: '首页', href: '/' }, { name: '漫画', href: '/comic/0' }];

  // Related comics (same category, excluding current)
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，该漫画不存在或已被下架</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <WebPageSchema
        name={`${comic.title} - 飞飞漫画网`}
        description={comic.description}
        url={`/comic/${comic.id}`}
        datePublished="2024-01-15"
        dateModified={comic.updatedAt}
      />
      <ArticleSchema
        headline={`${comic.title} - ${comic.originalTitle}`}
        description={comic.description}
        author={comic.author}
        datePublished="2024-01-15"
        dateModified={comic.updatedAt}
        image={comic.cover}
        url={`/comic/${comic.id}`}
      />
      <BreadcrumbListSchema items={breadcrumbItems} />

      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <section className="px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {/* Comic Detail Header */}
        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="md:w-64 flex-shrink-0">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 飞飞漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                  {comic.status === '已完结' && (
                    <span className="absolute top-3 left-3 bg-secondary-500 text-white text-xs px-3 py-1 rounded-md">
                      已完结
                    </span>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-400 mb-4">{comic.originalTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 text-sm text-primary-500 font-medium">
                    <i className="ri-star-fill"></i> {comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{comic.author} 著</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{formatViews(comic.views)} 次阅读</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className={`text-sm font-medium ${comic.status === '已完结' ? 'text-secondary-500' : 'text-primary-500'}`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  <button className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-7 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer">
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读
                  </button>
                  <button className="whitespace-nowrap inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-7 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    加入书架
                  </button>
                  <button className="whitespace-nowrap inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-7 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold font-heading text-foreground-950">
                章节列表 <span className="text-sm text-foreground-400 font-normal font-body">（共 {comic.chapters} 话）</span>
              </h2>
              <div className="flex items-center gap-2">
                <button className="whitespace-nowrap text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                  正序 <i className="ri-arrow-up-down-line"></i>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {comic.chapterList.map((chapter) => (
                <a
                  key={chapter.num}
                  href={`/comic/${comic.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-200 transition-colors cursor-pointer group"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      第{chapter.num}话 {chapter.title}
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">{chapter.date}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 flex-shrink-0 ml-2"></i>
                </a>
              ))}
            </div>

            {/* Show more if needed */}
            <div className="text-center mt-8">
              <button className="whitespace-nowrap inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer">
                查看全部 {comic.chapters} 话
                <i className="ri-arrow-down-s-line"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold font-heading text-foreground-950">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 transition-colors cursor-pointer"
                >
                  更多 {category?.name} 漫画 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 飞飞漫画网`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <span className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-md">
                        {rc.rating}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}